import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {
	Slider,
	Select,
	Input,
	Button,
	SelectOption, Tooltip, Loader, Toast
} from '@kaleyra/emmy';
import Styles from './UserGroup.module.scss';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import {isEmpty} from "lodash";
import StandardTitle from "../../../../../components/StandardTitle";
import PasswordValidator from "../../components/PasswordValidatorDark";
import {validatePassword12To64, validateTitle} from "../../../../../utils";
import {ALPHANUMERIC_WITHOUT_LEADING_TRAILING_HYPHEN_UNDERSCORE} from "../../../../../constants";

export type MapStateToPropsType = {
	params: any;
	bridgeNumbers:any,
	bridgeNumberInProgress:boolean|undefined
};

export type MapDispatchToPropsType = {
	closeModal: () => void,
	getBridgeNumbers: (params:any) => void,
	addUserGroup : (reqData: any, cb?: (res: any, success: boolean) => void) => void,
	addSipCredential : (reqData: any, filters:any, dynamicUrlData:string, cb?: (res: any, success: boolean) => void) => void,
	removeSipCredentialInstance : () => void
	removeUserGroupInstance : () => void
};

export const validateSipUserNameDomain = (val:string,maxCharLimit:number=32,minCharLimit:number=3) => {
	if(val === undefined) return false;
	if (val.trim().length < minCharLimit || val.trim().length > maxCharLimit) return false;
	let regex = ALPHANUMERIC_WITHOUT_LEADING_TRAILING_HYPHEN_UNDERSCORE.test(val);
	return regex;
}

const FilterModal: React.FC<MapStateToPropsType & MapDispatchToPropsType> = (
	{
		closeModal,
		// loadDropdownData,
		params,
		addUserGroup,
		addSipCredential,
		removeSipCredentialInstance,
		removeUserGroupInstance,
		getBridgeNumbers,
		bridgeNumbers,
		bridgeNumberInProgress
	}) => {
	const [title, setTitle] = useState<undefined|string>(undefined);
	const [userName, setUserName] = useState<undefined|string>(undefined);
	const [password, setPassword] = useState<undefined|string>(undefined);
	const [confirmPassword, setConfirmPassword] = useState<undefined|string>(undefined);
	const [showPasswordMatch, setShowPasswordMatch] = useState<boolean>(false);
	const [changePass, setChangePass] = useState<boolean>(false);
	const [cli, setCli] = useState<string|undefined>(undefined);
	const { t } = useTranslation();
	const { userGroupData, onlyAddCredential = false } = params;

	useEffect(()=>{
		getBridgeNumbers({voice_sip:1});
		if(!isEmpty(userGroupData)){
			setTitle(userGroupData.credential_group_name);
		}
		return () => {
			if(!onlyAddCredential) removeSipCredentialInstance();
			else removeUserGroupInstance();
		}
	},[])
	const handleSave = useCallback(() => {
		if(!title || !userName || !password || !cli) return;
		if(!onlyAddCredential){
			addUserGroup({
					credential_group_name:title,
					username:userName,
					password,
					cli:cli,
			}
			,(res,success)=>{
				if(success) Toast(t('KLR00484'),'success');
				else if(res?.data?.message) Toast(res?.data?.message,'error');
			});
		}else{
			addSipCredential({},
				{
					username:userName,
					password,
					cli:cli,
					credential_group_id:userGroupData.credential_group_id
				},
				"",
				(res,success) => {
					if (success) Toast(t('KLR00488'), 'success');
					else if(res?.data?.message) Toast(res?.data?.message,'error');
				}
				// data.credential_group_id
			);
		}
		closeModal();
	},[title,userName,password,cli]);

	const enableSaveButton = useMemo(() => {
		if(!title || !userName || !password || !cli || !confirmPassword) return false;
		if(!validatePassword12To64(password)) return false;
		if(password !== confirmPassword) return false;
		if(!validateTitle(title) || !validateSipUserNameDomain(userName)) return false;
		return true;
	},[title,userName,password,confirmPassword,cli]);

	const renderFooter = () => {
		return (
			<div className={Styles.footer}>
				<span className={Styles.cancelBtn}>
					<Button type="tertiary" onClick={closeModal}>
						{t('mainCancel')}
					</Button>
				</span>
				<Button
					disabled={!enableSaveButton}
					type="primary"
					onClick={handleSave}
				>
					{t('mainSave')}
				</Button>
			</div>
		);
	};

	const invalidPasswordErrorLabel = useMemo(() =>{
		if(!changePass || password === undefined) return "";
		if(!validatePassword12To64(password)) return t('Invalid password');
		return undefined;
	},[changePass,password]);

	const validatePasswordMatch = useMemo(()=>{
		return password === confirmPassword;
	},[password,confirmPassword]);
	return (
		<Slider
			title={onlyAddCredential ? t('KLR00456') : t('KLR00443')}
			containerId="root-modal"
			onClose={closeModal}
			visibility={true}
			footer={renderFooter()}
		>
			{ (bridgeNumberInProgress === undefined || bridgeNumberInProgress) && <Loader/>}
			{ bridgeNumberInProgress === false && <div className={cn(Styles.container, 'addUserGroupModal')}>
				<StandardTitle
					label="mainTitle"
					placeholder="mainTitle"
					title={title}
					setTitle={setTitle}
					disable={onlyAddCredential}
				/>
				<div className={Styles.compSubHeader}>
					{t('KLR00441')}
				</div>
				<StandardTitle
					label="KLR00444"
					placeholder="KLR00444"
					title={userName}
					setTitle={setUserName}
					maxCharLimit={32}
					allowLeadingAndTrailingHyphensUnderscores={false}
					allowSpaceInRegex={false}
					autoComplete="new-password"
				/>
				<Tooltip
					title={<PasswordValidator password={password||""} />}
					placement="left"
					trigger= "focus"
					color="#212B36"
					overlayInnerStyle={{borderRadius:"4px"}}
				>
					<Input
						placeholder={t('password')}
						inputClassName={Styles.ipBox}
						className={Styles.mrgBtm}
						label={t('password')}
						password={true}
						value={password}
						showBottomLabelSpace={ changePass && password!==undefined && !validatePassword12To64(password || "")}
						bottomLabelType={
							changePass && password!==undefined && !validatePassword12To64(password || "")? 'error' : undefined
						}
						bottomLabel={ invalidPasswordErrorLabel }
						onFocus={() => {
							setChangePass(false);
						}}
						onChange={(e: any) => {
							setPassword(e.target.value);
						}}
						onBlur={() => {
							setChangePass(true);
						}}
						autoComplete="new-password"
					/>
				</Tooltip>
				<Input
					placeholder={t('KLR00447')}
					inputClassName={Styles.ipBox}
					className={cn(Styles.mrgBtm)}
					label={t('KLR00447')}
					password={true}
					iconRender={()=><></>}
					value={confirmPassword}
					showBottomLabelSpace={ showPasswordMatch && !validatePasswordMatch }
					bottomLabelType={ (showPasswordMatch && !validatePasswordMatch) ? "error" : undefined}
					bottomLabel={
						(showPasswordMatch && !validatePasswordMatch) ? t("passwords don't match") : false
					}
					onFocus={() => {
						setShowPasswordMatch(false)
					}}
					onBlur={() => {
						setShowPasswordMatch(true)
					}}
					onChange={(e: any) => {
						// setShowPasswordMatch(false);
						setConfirmPassword(e.target.value);
					}}
				/>
				<Select
					label={t('KLR00448')}
					placeholder={t('KLR00448')}
					value={cli}
					onChange={(e) => {
						setCli((e||"").toString());
					}}
				>
					<SelectOption
						key={'Default'}
						value={"Default"}
						className={Styles.defaultSelect}
					>
						{t("KLR00449")}
					</SelectOption>
					{!isEmpty(bridgeNumbers?.bridge_numbers) && Object.keys(bridgeNumbers.bridge_numbers).map( (key:any) => {
						return (
							<SelectOption
								key={key}
								value={key}
							>
								{bridgeNumbers.bridge_numbers[key]}
							</SelectOption>
						);
					})}
				</Select>
			</div> }
		</Slider>
	);
};

export default FilterModal;
